import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_PREFIX = 'columns:';

/**
 * Table column visibility, persisted per user in localStorage.
 *
 * Index pages all share the same `Set<string>` + show/hide contract, so this
 * hook is a drop-in replacement for the `useState<Set<string>>` they declare
 * inline — the only difference being that the choice survives a reload.
 *
 * @param storageKey page-scoped key, e.g. `companies:index`
 * @param defaultColumns columns shown when nothing is stored
 * @param knownColumns every column the page can render; anything else is
 *   dropped on read so a column removed in a later deploy cannot linger in a
 *   user's stored set and render an empty cell.
 */
export function useColumnVisibility(
    storageKey: string,
    defaultColumns: readonly string[],
    knownColumns: readonly string[],
): {
    visibleColumns: Set<string>;
    showColumn: (columnId: string) => void;
    hideColumn: (columnId: string) => void;
    resetColumns: () => void;
} {
    const fullKey = `${STORAGE_PREFIX}${storageKey}`;

    // Recompute only when the page's column catalogue actually changes, not on
    // every render — the arrays are usually fresh literals from the caller.
    const knownKey = knownColumns.join(',');
    const defaultKey = defaultColumns.join(',');

    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => readStored(fullKey, defaultColumns, knownColumns));

    // A stored set written before this deploy may reference columns that no
    // longer exist; re-filter whenever the catalogue changes.
    useEffect(() => {
        setVisibleColumns((current) => {
            const filtered = new Set([...current].filter((id) => knownColumns.includes(id)));
            if (filtered.size === current.size) {
                return current;
            }
            return filtered.size > 0 ? filtered : new Set(defaultColumns);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [knownKey, defaultKey]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            window.localStorage.setItem(fullKey, JSON.stringify([...visibleColumns]));
        } catch {
            // Private mode or a full quota — persistence is a convenience, so
            // fall back to in-memory state rather than breaking the page.
        }
    }, [fullKey, visibleColumns]);

    const showColumn = useCallback((columnId: string) => {
        setVisibleColumns((current) => {
            if (current.has(columnId)) {
                return current;
            }
            const next = new Set(current);
            next.add(columnId);
            return next;
        });
    }, []);

    const hideColumn = useCallback((columnId: string) => {
        setVisibleColumns((current) => {
            if (!current.has(columnId)) {
                return current;
            }
            const next = new Set(current);
            next.delete(columnId);
            return next;
        });
    }, []);

    const defaults = useMemo(() => [...defaultColumns], [defaultKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetColumns = useCallback(() => {
        setVisibleColumns(new Set(defaults));
    }, [defaults]);

    return { visibleColumns, showColumn, hideColumn, resetColumns };
}

function readStored(fullKey: string, defaultColumns: readonly string[], knownColumns: readonly string[]): Set<string> {
    if (typeof window === 'undefined') {
        return new Set(defaultColumns);
    }

    try {
        const stored = window.localStorage.getItem(fullKey);
        if (!stored) {
            return new Set(defaultColumns);
        }

        const parsed: unknown = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
            return new Set(defaultColumns);
        }

        const valid = parsed.filter((id): id is string => typeof id === 'string' && knownColumns.includes(id));

        // An empty stored set would render a table with no columns at all —
        // treat it as "nothing usable stored" and fall back to the defaults.
        return valid.length > 0 ? new Set(valid) : new Set(defaultColumns);
    } catch {
        return new Set(defaultColumns);
    }
}
